import { useQuery } from '@tanstack/react-query';
import api from '../api/client';

/**
 * Feature Flags / Visibility Toggles — reads the public GET /api/feature-flags
 * endpoint (see server/src/controllers/featureFlagsController.js). Used to
 * hide nav links (Navbar.jsx) and redirect away from gated routes (App.jsx)
 * on the frontend. This is a UX nicety, not the actual security boundary —
 * middleware/featureFlag.js's server-side 404 gate is what makes a disabled
 * section genuinely inaccessible even to someone who guesses/bookmarks its
 * URL directly.
 *
 * Defaults every flag to `true` while the initial request is in flight (and
 * on error) so a slow/failed feature-flags fetch never flashes nav links in
 * and out, or hides a section that's actually enabled — the server-side
 * gate is the real enforcement either way.
 */
const DEFAULT_FLAGS = {
  onlineAdBookingEnabled: true,
  printAdBookingEnabled: true,
  printMagazineStoreEnabled: true,
  printMagazineArchiveEnabled: true,
  // Magazine nav menu + Monthly Magazine reader (see models/Settings.js).
  magazineEnabled: true,
  // Daily Automated Podcast — see server/src/controllers/featureFlagsController.js's
  // header for why this one flag's effective server value can differ from
  // what's stored (a logged-in admin always sees it regardless).
  podcastEnabled: true,
};

export function useFeatureFlags() {
  const { data } = useQuery({
    queryKey: ['feature-flags'],
    queryFn: async () => (await api.get('/feature-flags')).data.featureFlags,
    staleTime: 60_000,
  });
  return { ...DEFAULT_FLAGS, ...data };
}
